import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  Form,
  Container,
  Row,
  Col,
  FormGroup,
} from "react-bootstrap";
import {
  getPackagePricingById,
  updatePackagePricing,
} from "../../actions/packagePricingAction";
import FullPageLoader from "../../components/FullPageLoader/FullPageLoader";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";

const packagePricingSchema = Yup.object().shape({
  package: Yup.string()
    .oneOf(["monthly", "quarterly", "yearly"], "Invalid package type")
    .required("Package is required"),
  numberOfConsultations: Yup.number()
    .required("Number of consultations is required")
    .min(1, "Number of consultations must be at least 1")
    .integer("Number of consultations must be a whole number"),
  discountType: Yup.string()
    .oneOf(["percentage"], "Invalid discount type")
    .required("Discount type is required"),
  discountPercentage: Yup.number()
    .required("Discount percentage is required")
    .moreThan(0, "Percentage must be greater than 0")
    .max(99, "Percentage cannot exceed 99"),
});

const EditPackagePricing = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loader, setLoader] = useState(false);
  const [packagePricingData, setPackagePricingData] = useState({});

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      package: packagePricingData?.package || "quarterly",
      numberOfConsultations: packagePricingData?.numberOfConsultations || "",
      discountType: packagePricingData?.discountType === "fixed" ? "percentage" : (packagePricingData?.discountType || "percentage"),
      discountPercentage: packagePricingData?.discountType === "percentage" ? (packagePricingData?.discountPercentage ?? null) : null,
      description: packagePricingData?.description || "",
      isActive: packagePricingData?.isActive !== undefined ? packagePricingData.isActive : true,
    },
    validationSchema: packagePricingSchema,
    onSubmit: async (values) => {
      setLoader(true);
      try {
        const payload = {
          package: values.package,
          numberOfConsultations: parseInt(values.numberOfConsultations),
          discountType: "percentage",
          discountPercentage: parseFloat(values.discountPercentage),
          description: values.description,
          isActive: values.isActive,
        };

        const res = await updatePackagePricing(id, payload);
        if (res?.data?.success) {
          navigate("/package-pricing");
          toast.success(res.data.message);
        } else {
          toast.error(res?.data?.message || "Failed to update package pricing");
        }
      } catch (error) {
        console.error("Error updating package pricing:", error);
        toast.error(error?.response?.data?.message || "Error updating package pricing");
      }
      setLoader(false);
    },
  });

  useEffect(() => {
    const getPricingById = async () => {
      setLoader(true);
      try {
        let res = await getPackagePricingById(id);
        if (res?.data?.success && res.data.data) {
          setPackagePricingData(res.data.data);
        }
      } catch (error) {
        console.error("Error fetching package pricing:", error);
        toast.error("Error fetching package pricing");
      }
      setLoader(false);
    };
    if (id) {
      getPricingById();
    }
  }, [id]);

  return (
    <>
      {loader ? (
        <FullPageLoader />
      ) : (
        <div className="pt-2 pt-md-3 cms-updated-style edit-package-pricing">
          <Container fluid>
            <Row>
              <Col md="12">
                <h3>Add Content Page</h3>
                <Card className="filter-card card">
                  <Card.Header>
                    <div className="d-flex align-items-center justify-content-between table-head">
                      <Card.Title className="text-white" as="h4">
                        Edit Package Pricing
                      </Card.Title>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col>
                        <Form onSubmit={formik.handleSubmit}>
                          <Row>
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  Package Type <span className="text-danger">*</span>
                                </Form.Label>
                                 <div className="select-with-arrow">
                                <Form.Control
                                  as="select"
                                  name="package"
                                  value={formik.values.package || ""}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={formik.touched.package && formik.errors.package}
                                >
                                  <option value="monthly">Monthly</option>
                                  <option value="quarterly">Quarterly</option>
                                  <option value="yearly">Yearly</option>
                                </Form.Control>
                                </div>
                                {formik.touched.package && formik.errors.package ? (
                                  <span>
                                    <label className="pl-1 text-danger">
                                      {formik.errors.package}
                                    </label>
                                  </span>
                                ) : null}
                              </Form.Group>
                            </Col>
                              <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>
                                 Number of Consultations <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                  type="number"
                                  name="numberOfConsultations"
                                  placeholder="Number of Consultations"
                                  value={formik.values.numberOfConsultations || ""}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  isInvalid={
                                    formik.touched.numberOfConsultations &&
                                    formik.errors.numberOfConsultations
                                  }
                                  min="1"
                                />
                                {formik.touched.numberOfConsultations &&
                                  formik.errors.numberOfConsultations ? (
                                  <span>
                                    <label className="pl-1 text-danger">
                                      {formik.errors.numberOfConsultations}
                                    </label>
                                  </span>
                                ) : null}
                              </Form.Group>
                            </Col>
                           
                          </Row>
                        <Row>
                           <Col md={6}>
                              <Form.Group className="mb-3">
                                <Form.Label>
                                  Discount Type <span className="text-danger">*</span>
                                </Form.Label>
                                <div className="select-with-arrow">
                                  <Form.Control
                                    as="select"
                                    name="discountType"
                                    value={formik.values.discountType || "percentage"}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    isInvalid={
                                      formik.touched.discountType && formik.errors.discountType
                                    }
                                  >
                                    <option value="percentage">Percentage (%)</option>
                                  </Form.Control>
                                </div>
                                {formik.touched.discountType && formik.errors.discountType ? (
                                  <span>
                                    <label className="pl-1 text-danger">
                                      {formik.errors.discountType}
                                    </label>
                                  </span>
                                ) : null}
                              </Form.Group>
                            </Col>
                             <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>
                                    Discount Percentage <span className="text-danger">*</span>
                                  </Form.Label>
                                  <Form.Control
                                    type="number"
                                    name="discountPercentage"
                                    placeholder="Enter percentage (1-99)"
                                    value={formik.values.discountPercentage ?? ""}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    isInvalid={
                                      formik.touched.discountPercentage &&
                                      formik.errors.discountPercentage
                                    }
                                    min="1"
                                    max="99"
                                  />
                                  {formik.touched.discountPercentage &&
                                    formik.errors.discountPercentage ? (
                                    <span>
                                      <label className="pl-1 text-danger">
                                        {formik.errors.discountPercentage}
                                      </label>
                                    </span>
                                  ) : null}
                                </Form.Group>
                            </Col>
                           </Row>

                          {/* <Row>
                            <Col md={12}>
                              <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  name="description"
                                  placeholder="Enter description (optional)"
                                  value={formik.values.description || ""}
                                  onChange={formik.handleChange}
                                />
                              </Form.Group>
                            </Col>
                          </Row> */}

                          <FormGroup>
                            <Row>
                              <Col md={4}>
                                <label className="label-font">Status</label>
                              </Col>
                              <Col md={8} className="check-inline d-flex">
                                <label className="right-label-radio mr-3 mb-2">
                                  Active
                                  <input
                                    name="status"
                                    type="radio"
                                    checked={formik.values.isActive === true}
                                    value={true}
                                    onChange={(e) =>
                                      formik.setFieldValue("isActive", true)
                                    }
                                  />
                                  <span className="checkmark"></span>
                                </label>
                                <label className="right-label-radio mr-3 mb-2">
                                  Inactive
                                  <input
                                    name="status1"
                                    type="radio"
                                    checked={formik.values.isActive === false}
                                    value={false}
                                    onChange={(e) =>
                                      formik.setFieldValue("isActive", false)
                                    }
                                  />
                                  <span className="checkmark"></span>
                                </label>
                              </Col>
                            </Row>
                          </FormGroup>

                          <Button className="btn-filled" type="submit">
                            Submit
                          </Button>
                        </Form>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </>
  );
};

export default EditPackagePricing;
